// Angular Imports
import { NgIf, NgFor } from '@angular/common'
import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	ViewChild,
	effect,
	inject,
	signal,
} from '@angular/core'
// Angular Material Imports
import { MatDialog } from '@angular/material/dialog'
// Shared Imports
import { ToolsService } from '@shared/services'
import {
	AlertComponent,
	ButtonComponent,
	IconButtonComponent,
	IconComponent,
} from '@shared/components'
import { EIcons, FilePreview } from '@shared/models'
import { BytesPipe } from '@shared/pipes'

const sharedComponents = [ButtonComponent, IconComponent, IconButtonComponent]

const sharedPipes = [BytesPipe]

@Component({
	standalone: true,
	selector: 'app-upload-images',
	imports: [NgIf, NgFor, ...sharedComponents, ...sharedPipes],
	templateUrl: './upload-images.component.html',
	styleUrls: ['./upload-images.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadImagesComponent {
	readonly #tools = inject(ToolsService)
	readonly #dialog = inject(MatDialog)
	readonly EIcons = EIcons
	readonly allowedFileExtensions: string[] = ['png', 'jpg', 'jpeg', 'jfif', 'webp', 'heif']
	@ViewChild('selectPhotoInput') selectPhotoInput!: ElementRef
	@ViewChild('takePhotoInput') takePhotoInput!: ElementRef
	@Input() set showTakePhotoButton(value: boolean) {
		this.isTakePhotoEnable.set(value)
	}
	@Input() set showSelectPhotoButton(value: boolean) {
		this.isSelectPhotoEnable.set(value)
	}
	@Input() set nextFileName(value: string) {
		this.useFileName.set(value)
	}
	@Output() images = new EventEmitter<File[]>()

	files = signal<File[]>([])
	filesPreview = signal<FilePreview[]>([])

	isTakePhotoEnable = signal<boolean>(false)
	isSelectPhotoEnable = signal<boolean>(true)
	useFileName = signal<string>('')

	constructor() {
		effect(
			() => {
				const files = this.files()
				files.length && this.images.emit(this.files())
			},
			{ allowSignalWrites: true }
		)
	}

	onSelectPhoto() {
		const inputElement: any = this.selectPhotoInput?.nativeElement
		inputElement.accept = 'image/*'
		inputElement.click()
		inputElement.value = ''
	}

	onTakePhoto() {
		const inputElement: any = this.takePhotoInput?.nativeElement
		inputElement.capture = 'environment'
		inputElement.accept = 'image/*'
		inputElement.click()
		inputElement.value = ''
	}

	onHandleFiles(event: Event) {
		const inputElement = event.target as HTMLInputElement
		const files: File[] = Array.from(inputElement?.files || [])
		const notLoadedFilesNames: string[] = []

		if (files.length) {
			files.map(async (file, index) => {
				const newFileName = this.useFileName()
					? `${this.useFileName()}-${file.name}`
					: `${file.name}`
				const fileRename = this.#tools.renameFile({ file, newFileName })
				const filePreview: FilePreview = await this.#tools.buildFilePreview(fileRename)

				if (this.#isValidFileExtension(fileRename)) {
					this.files.update((previous) => [fileRename, ...previous])
					this.filesPreview.update((previous) => [filePreview, ...previous])
				} else {
					notLoadedFilesNames.push(newFileName)
				}

				if (index === files.length - 1 && notLoadedFilesNames.length) {
					this.#openAlertDialog()
				}
			})
		}
	}

	removeFilePreview(file: File): void {
		const filesFiltered = this.files().filter((_file) => _file.name !== file.name)
		const filesPreviewFiltered = this.filesPreview().filter(
			(_filePreview) => _filePreview.file.name !== file.name
		)
		this.files.set(filesFiltered)
		this.filesPreview.set(filesPreviewFiltered)
	}

	#isValidFileExtension(file: File) {
		const extension: string | undefined = file.name.split('.').pop()?.toLowerCase()
		return extension ? this.allowedFileExtensions.includes(extension) : false
	}

	#openAlertDialog(): void {
		this.#dialog.open(AlertComponent, {
			// minWidth: '350px',
			maxWidth: '350px',
			maxHeight: '95vh',
			data: {
				type: 'error',
				message: 'Algunas imágenes no poseen el formato correcto!',
			},
			panelClass: ['Dialog'],
			autoFocus: false,
		})
	}
}
